import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit
} from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { Dictionary } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import {
    filter,
    map,
    take,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as fromSettings from '../../company-setting';
import * as fromDataServices from '../../data-services';
import * as fromGroups from '../../groups/store';
import * as fromLookups from '../../lookups';
import { ITimeZone } from '../../lookups';
import { PayrollItem } from '../../payroll/item/store';
import * as fromShared from '../../shared';
import { ResponseStatus } from '../../shared';
import { LoggingService } from '../../shared/services/logging';
import { IUserInfo, UserRoleEnum } from '../../user-info';
import * as fromUser from '../../user-info/store';
import moment from 'moment';
@Component({
    selector: 'ef-manage-users-container',
    templateUrl: './manage-users-container.component.html',
    styleUrls: ['./manage-users-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ManageUsersContainerComponent implements OnInit, OnDestroy {
    qboEnabled$: Observable<boolean>;
    ManageUsersModelDS: EntityCollectionService<fromDataServices.ManageUsers.LoginModel>;
    ManageUsersModel$: Observable<fromDataServices.ManageUsers.LoginModel[]>;
    costCenters$: Observable<fromShared.SelectItem[]>;
    users$: Observable<Array<fromDataServices.ManageUsers.LoginModel>>;
    userInfo$: Observable<IUserInfo>;
    supervisors$: Observable<fromShared.SelectItem[]>;
    userTypes$: Observable<fromShared.SelectItem[]>;
    splitdays$: Observable<fromShared.SelectItem[]>;
    daysOfWeek$: Observable<fromShared.SelectItem[]>;
    timeZones$: Observable<ITimeZone[]>;
    timeZonesSelectItems$: Observable<fromShared.SelectItem[]>;
    creditCards$: Observable<fromShared.SelectItem[]>;
    activeServiceItems$: Observable<fromShared.SelectItem[]>;
    serviceItems$: Observable<fromShared.SelectItem[]>;
    payrollItems$: Observable<PayrollItem[]>;
    payrollFrequency$: Observable<fromShared.SelectItem[]>;
    userIdFormats$: Observable<fromShared.SelectItem[]>;
    messageDeliveryTypes$: Observable<fromShared.SelectItem[]>;
    mobileProviders$: Observable<fromShared.SelectItem[]>;
    personSelectItems$: Observable<fromShared.SelectItem[]>;
    emailEnabled$: Observable<boolean>;
    bulkUpdateEnabled$: Observable<boolean>;
    SelectedLoginsSubject = new Subject<
        fromDataServices.ManageUsers.LoginModel[]
    >();
    UpdateBulkLoginsSubject = new ReplaySubject<any>(1);
    SaveLoginSubject = new Subject<any>();
    ShowInactiveSubject = new Subject<boolean>();
    process$: Observable<fromShared.Process>;
    selectedErrorWithStatus$: Observable<fromShared.IStatusMessage>;
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    public tabHeight = `${65}vh`;
    private active = true;
    constructor(
        private log: LoggingService,
        public entityServices: EntityServices,
        private store: Store
    ) {
        this.log.verbose({ type: 'ManageUsersContainerComponent::' });
        this.ManageUsersModelDS =
            entityServices.getEntityCollectionService<fromDataServices.ManageUsers.LoginModel>(
                fromDataServices.ManageUsers.entityName
            );

        this.store.dispatch(new fromLookups.GetCreditCards('CreditCard'));
    }
    ngOnInit(): void {
        this.ManageUsersModelDS.clearCache();
        this.ManageUsersModelDS.getWithQuery({
            ShowInactive: 'false',
            UserFilterID: '7'
        });

        this.store.dispatch(
            fromSettings.getCompanySetting({ SettingType: 'OverrideQBMail' })
        );
        //this.store.dispatch(new fromUser.LoadUserInfo()); // commented out for #5336
        this.store.dispatch(new fromLookups.LoadUserGroups());
        this.store.dispatch(new fromLookups.LoadSplitday());
        this.store.dispatch(new fromLookups.LoadMobileProviders());
        this.store.dispatch(new fromLookups.LoadEntityFilter('User'));
        this.store.dispatch(new fromLookups.LoadServiceItems(false));
        //this.store.dispatch(new fromLookups.LoadPayrollItems()); // commented out for #5336

        this.store.dispatch(new fromLookups.LoadPayrollFrequency());
        this.store.dispatch(new fromLookups.LoadUserIdFormats());
        this.store.dispatch(new fromLookups.GetMessageDeliveryTypes());
        this.store.dispatch(
            new fromGroups.LoadGroupMembers({
                GroupID: UserRoleEnum.Supervisor,
                IsGroupMember: true
            })
        );

        this.setupObservables();

        combineLatest([this.process$, this.ShowErrorSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
        this.setupSubscriptions();
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.selectedErrorWithStatus$ = this.store.pipe(
            select(fromShared.Errors.selectErrorWithStatus)
        );
        this.qboEnabled$ = this.store.pipe(select(fromSettings.isQBOEnabled));

        this.emailEnabled$ = this.store.pipe(
            select(fromSettings.getSettingBooleanValue('OverrideQBMail'))
        );
        this.daysOfWeek$ = this.store.pipe(
            select(fromLookups.getDaysOfWeek),
            map(items =>
                items.map(item => ({
                    label: item.DayOfWeekDesc,
                    value: item.DayOfWeekID
                }))
            )
        );
        this.timeZones$ = this.store.pipe(select(fromLookups.getTimeZones));
        this.timeZonesSelectItems$ = this.store.pipe(
            select(fromLookups.getTimeZones),
            map(items =>
                items.map(item => ({ label: item.DisplayName, value: item.Id }))
            ),
            tap(items =>
                this.log.verbose({
                    type: 'ManageUsersContainerComponent:timeZonesSelectItems$',
                    items
                })
            )
        );
        this.messageDeliveryTypes$ = this.store.pipe(
            select(fromLookups.selectMessageDeliveryTypes),
            filter(x => !!x),
            map(items =>
                items.map(item => ({
                    label: item.Messagedeliverydesc,
                    value: item.Messagedeliveryid
                }))
            )
        );
        this.creditCards$ = this.store.pipe(
            select(fromLookups.getCreditCards),
            filter(x => !!x),
            map(items =>
                items.map(item => ({
                    label: item.AccountCode,
                    value: item.AccountListID
                }))
            )
        );
        this.supervisors$ = this.store.pipe(
            select(fromGroups.getGroupMembers),
            filter(x => !!x),
            map(items =>
                items.map(item => ({
                    label: item.UserName,
                    value: item.ListID
                }))
            )
        );
        this.userInfo$ = this.store.pipe(
            select(fromUser.getUserInfo),
            filter(x => !!(x && x.EmployeeListID))
        );
        this.serviceItems$ = this.store.pipe(
            select(fromLookups.getServiceItemsLookup),
            map(items =>
                items.map(item => ({
                    label: item.ServiceItem,
                    value: item.SIListID
                }))
            )
        );
        this.activeServiceItems$ = this.store.pipe(
            select(fromLookups.getServiceItemsLookup),
            map(items =>
                items
                    .filter(x => x.IsActive)
                    .map(item => ({
                        label: item.ServiceItem,
                        value: item.SIListID
                    }))
            )
        );
        this.payrollItems$ = this.store.pipe(
            select(fromLookups.getPayrollItemsLookup)
        );
        this.costCenters$ = this.store.pipe(
            select(fromLookups.getCostCenters),
            map(items =>
                items.map(item => ({
                    label: item.GroupName,
                    value: item.GroupID
                }))
            )
        );
        this.userTypes$ = this.store.pipe(
            select(fromLookups.getEntityFilters, { EntityType: 'User' }),
            map(items =>
                items.map(item => ({
                    label: item.FilterDesc.substring(0, 3),
                    value: item.FilterDesc
                }))
            )
        );
        this.splitdays$ = this.store.pipe(
            select(fromLookups.getSplitdays),
            map(items =>
                items.map(item => ({
                    label: item.SplitDayDesc,
                    value: item.SplitDayID
                }))
            )
        );
        this.payrollFrequency$ = this.store.pipe(
            select(fromLookups.getPayrollFrequency),
            map(items =>
                items.map(item => ({
                    label: item.Payrollfrequency.toString(),
                    value: item.Payrollfrequency
                }))
            )
        );
        this.userIdFormats$ = this.store.pipe(
            select(fromLookups.getUserIdFormats),
            map(items =>
                items.map(item => ({
                    label: item.Useridformat_Desc,
                    value: item.Useridformat_ID
                }))
            )
        );

        this.mobileProviders$ = this.store.pipe(
            select(fromLookups.selectMobileProviders),
            map(items =>
                items.map(item => ({
                    label: item.MobileproviderName,
                    value: item.MobileproviderName
                }))
            )
        );

        const source = [];
        this.ManageUsersModel$ = combineLatest([
            this.ManageUsersModelDS.entities$,
            this.supervisors$,
            this.daysOfWeek$,
            this.serviceItems$,
            this.payrollItems$,
            this.splitdays$,
            this.creditCards$,
            this.messageDeliveryTypes$,
            this.costCenters$
        ]).pipe(
            // map(x => x),
            map(([items, supers, days, si, pi, sd, cc, rt, cost]: any) =>
                items.map(item => ({
                    ...item,
                    SupervisorName:
                        supers.findIndex(
                            x => x.value === item.SupervisorListID
                        ) >= 0
                            ? supers.find(
                                  x => x.value === item.SupervisorListID
                              ).label
                            : '',
                    WeekStartName:
                        days.findIndex(
                            x => x.value === item.FirstDayofWeekID
                        ) >= 0
                            ? days.find(x => x.value === item.FirstDayofWeekID)
                                  .label
                            : '',
                    DefaultServiceItemName:
                        si.findIndex(x => x.value === item.SIListID) >= 0
                            ? si.find(x => x.value === item.SIListID).label
                            : '',
                    DefaultPayrollItemName:
                        pi.findIndex(x => x.PIListID === item.PIListID) >= 0
                            ? pi.find(x => x.PIListID === item.PIListID)
                                  .PayrollItem
                            : '',
                    SplitDayName:
                        sd.findIndex(
                            x => x.value === item.TimeCardSplitDayID
                        ) >= 0
                            ? sd.find(x => x.value === item.TimeCardSplitDayID)
                                  .label
                            : '',
                    CreditCardName:
                        cc.findIndex(x => x.value === item.CCAccountListID) >= 0
                            ? cc.find(x => x.value === item.CCAccountListID)
                                  .label
                            : '',
                    ReminderDeliveryName:
                        rt.findIndex(
                            x => x.value === item.ReminderdeliveryID
                        ) >= 0
                            ? rt.find(x => x.value === item.ReminderdeliveryID)
                                  .label
                            : '',
                    CostCenterName:
                        cost.findIndex(x => x.PIListID === item.CostCenterID) >=
                        0
                            ? cost.find(x => x.PIListID === item.CostCenterID)
                                  .label
                            : '',
                    TypeDesc: item.IsConsultant == 0 ? 'Emp' : 'Con',
                    ReleaseDate: moment(item.ReleaseDate).format('MM/DD/YYYY')
                }))
            ),
            tap(x =>
                this.log.verbose({
                    type: 'ManageUsersContainerComponent:ManageUsersModel$',
                    x
                })
            ),
            map(items => clone(items))
        );
        this.personSelectItems$ = this.ManageUsersModel$.pipe(
            map(items =>
                items.map(item => ({
                    label: item.EmployeeName,
                    value: item.EmployeeListID
                }))
            )
        );
        this.bulkUpdateEnabled$ = this.SelectedLoginsSubject.pipe(
            map(logins => logins.length > 0)
        );
    }
    setupSubscriptions() {
        this.bulkUpdateEnabled$.pipe(takeWhile(() => this.active)).subscribe();
        this.UpdateBulkLoginsSubject.pipe(
            takeWhile(() => this.active),
            tap(values =>
                this.log.verbose({
                    type: 'ManageUsersContainerComponent:UpdateBulkLoginsSubject',
                    values
                })
            ),
            withLatestFrom(this.SelectedLoginsSubject),
            withLatestFrom(this.timeZones$),
            filter(
                ([[values, logins], timezones]) => !!logins && logins.length > 0
            )
        ).subscribe(([[values, logins], timezones]) => {
            this.log.verbose({
                type: 'ManageUsersContainerComponent:UpdateBulkLoginsSubject',
                values,
                logins
            });
            Object.getOwnPropertyNames(values).forEach(prop => {
                if (!values[prop]) {
                    delete values[prop];
                }
            });
            const tz = timezones.find(x => x.Id === values.EmpTimeZone);
            logins.forEach(login => {
                const update = {
                    ...login,
                    ...values,
                    Tz_offsetfromutc_minutes:
                        tz && tz.OffsetMinutes
                            ? tz.OffsetMinutes
                            : login.Tz_offsetfromutc_minutes,
                    Tz_abbreviation: tz && tz.Id ? tz.Id : login.Tz_abbreviation
                };
                if (
                    values.AuthorizedHolidays !== null &&
                    values.AuthorizedHolidays !== undefined
                ) {
                    update.AuthorizedHolidays =
                        values.AuthorizedHolidays === '1' ? true : false;
                }
                update.CCAccountListID = Array.isArray(update.CCAccountListID)
                    ? update.CCAccountListID.join(',')
                    : (update.CCAccountListID ?? '');
                this.log.verbose({
                    type: 'ManageUsersContainerComponent:UpdateBulkLoginsSubject',
                    update
                });
                this.ManageUsersModelDS.upsert(update);
            });
        });

        this.SaveLoginSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.timeZones$)
        ).subscribe(([login, timezones]) => {
            const tz = timezones.find(x => x.Id === login.EmpTimeZone);
            this.log.verbose({
                type: 'ManageUsersContainerComponent:SaveLoginSubject',
                login
            });
            this.ManageUsersModelDS.upsert({
                ...login,
                CCAccountListID: Array.isArray(login.CCAccountListID)
                    ? login.CCAccountListID.join(',')
                    : login.CCAccountListID,
                Tz_offsetfromutc_minutes:
                    tz?.OffsetMinutes ?? login.Tz_offsetfromutc_minutes,
                Tz_abbreviation: tz?.Id ?? login.Tz_abbreviation
            });
        });
        this.ShowInactiveSubject.pipe(takeWhile(() => this.active)).subscribe(
            showInactive => {
                this.ManageUsersModelDS.clearCache();
                this.ManageUsersModelDS.getWithQuery({
                    ShowInactive: showInactive.toString(),
                    UserFilterID: '7'
                });
            }
        );
    }
    onSendCredentials(logins: fromDataServices.ManageUsers.LoginModel[]) {
        logins.forEach(login => {
            this.ManageUsersModelDS.update({
                ...login,
                CCAccountListID: Array.isArray(login.CCAccountListID)
                    ? login.CCAccountListID.join(',')
                    : login.CCAccountListID,
                Resetpswd: true
            })
                .pipe(
                    take(1),
                    filter(x =>
                        logins
                            .map(login => login.EmployeeListID)
                            .includes(x.EmployeeListID)
                    ),
                    filter(x => x.Status.StatusCode === ResponseStatus.Failed)
                )
                .subscribe(() => {
                    $(`#btnSendcredentials`).addClass('blinking');
                });
        });
    }
    onSelectUsers(logins: fromDataServices.ManageUsers.LoginModel[]) {
        this.log.verbose({
            type: 'ManageUsersContainerComponent:onSelectUsers',
            logins
        });
        this.SelectedLoginsSubject.next(logins);
    }
    handleOnOpen($event) {
        this.log.verbose({
            type: 'ManageUsersContainerComponent:handleOnOpen',
            $event
        });
        const baseHeight = 58;

        this.tabHeight = `${baseHeight - ($event.length - 1) * 14}vh`;
    }
}
